import * as is from 'utilities/is';
import { MESSAGES } from 'constants/communication';
import { makeUniqueId } from 'utilities/data';
import { PLAYER_EVENT, PLAYER_CONTROL_EVENT, APPLICATION_EVENT } from 'constants/events';
import { generateEmbed, toIFrameOptions } from 'utilities/embed';
import { onHapyakEvent } from 'utilities/communication';
import { IFrameApiProxy } from 'views/iframe/helper/iframe_api_proxy';
import { IFrameHelper } from 'views/iframe/helper/iframe_helper';

export default class IFramePlayer extends IFrameApiProxy {
    constructor({
        overridesUrl,
        ...options
    }) {
        const iframeEl = generateEmbed({
            type: 'iframe',
            src: overridesUrl,
            options: {
                ...toIFrameOptions(options),
                scaleToContainer: true
            } 
        });

        super(iframeEl);
        this.options = options;
        this.id = makeUniqueId();
        this.propertyCache = {};

        if (options.container && is.fn(options.container.appendChild)) {
            options.container.appendChild(this.iframeEl);
        }

        window.addEventListener('message', this.onIFrameReady);
    }

    onIFrameReady = onHapyakEvent((msg, event) => {
        if (msg.event === MESSAGES.EMBED_AVAILABLE) {
            if (event.source !== this.iframeEl.contentWindow) {
                return;
            }

            const { width, height } = this.options;
            
            this.iframeBridge = new IFrameHelper({
                id: this.id,
                embedEl: this.iframeEl,
                manageSize: !(height && height !== 'auto')
            });

            // need to assign event listeners for properties before setting this.eventBus
            this.iframeBridge.on(PLAYER_EVENT.PAUSE, this.cacheProperty('paused'));
            this.iframeBridge.on(PLAYER_EVENT.VOLUME_CHANGE, this.cacheProperty('volume'));
            this.iframeBridge.on(PLAYER_EVENT.TIME_UPDATE, this.cacheProperty('currentTime'));
            this.iframeBridge.on(PLAYER_EVENT.DURATION_CHANGE, this.cacheProperty('duration'));

            this.iframeBridge.on(APPLICATION_EVENT.SOURCE_LOADED, ({loadedSrc}) => {
                const { state, variables = {} } = loadedSrc;
                this.applyToCache(state);
                this.applyToCache({variables});
            });

            this.iframeBridge.on(PLAYER_EVENT.AVAILABLE, () => {
                if (this.propertyCache.currentTime > 0) {
                    this.iframeBridge.emit(PLAYER_CONTROL_EVENT.CURRENT_TIME, this.propertyCache.currentTime);
                }

                if (is.defined(this.propertyCache.volume)) {
                    this.iframeBridge.emit(PLAYER_CONTROL_EVENT.VOLUME, this.propertyCache.volume);
                }
            });

            this.iframeBridge.on(APPLICATION_EVENT.STATE_CHANGE, ({variables,state}) => this.applyToCache({ ...state, variables }));
            
            // assignment bridges player event listeners to iframe messages (see player_api->set eventBus())
            this.eventBus = this.iframeBridge;

            window.removeEventListener('message', this.onIFrameReady);
        }
    })

    applyToCache(toApply = {}) {
        Object.keys(toApply).forEach(k => {
            this.propertyCache[k] = toApply[k];
        })
    }

    reload() {
        // send source change message to iframes
    }

    showPreview() {
        this.iframeBridge && this.iframeBridge.showPreview();
    }

    updateSize() {
        this.iframeBridge.changeSize({ container: this.iframeBridge.lastDimension }, 0)
    }

    getVariable(name) {
        const variables = this.propertyCache.variables || {};
        return variables[name];
    }

    cacheProperty = propertyName => player => {
        this.propertyCache[propertyName] = player[propertyName];
    }

    get playerAdapter() {
        return this.propertyCache;
    }
}